import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@truefit/http-utils';
import useCurrentGameId from '@/shared/hooks/useCurrentGameId';

interface ResetClipGroupParams {
  playbyplayId: string;
  clipGroupId: number;
  inTime?: string;
  outTime?: string;
  softDelete?: boolean;
}

interface ResetClipGroupResponse {
  success: boolean;
  message: string;
  clipsReset: number;
}

export const useResetClipGroupMutation = () => {
  const queryClient = useQueryClient();
  const gameId = useCurrentGameId();

  return useMutation({
    mutationFn: async ({ playbyplayId, clipGroupId, inTime, outTime, softDelete }: ResetClipGroupParams) => {
      const response = await post<ResetClipGroupResponse>(
        `/v1.0/games/${gameId}/play-by-play/${playbyplayId}/clip-groups/${clipGroupId}/reset`,
        {
          ...(inTime && { inTime }),
          ...(outTime && { outTime }),
          ...(softDelete && { softDelete }),
        }
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate clip groups query to refresh the UI
      queryClient.invalidateQueries({
        queryKey: ['Games', gameId, 'PlayByPlay', variables.playbyplayId, 'ClipGroups']
      });

      // Also invalidate game-level queries
      queryClient.invalidateQueries({
        queryKey: ['Games', gameId, 'ClipGroups']
      });

      // Invalidate clips list (soft-deleted clips need to be removed from the list)
      queryClient.invalidateQueries({
        queryKey: ['Games', gameId, 'Clips']
      });
    },
  });
};

export default useResetClipGroupMutation;
